import { useEffect, useState, useCallback } from "react";
import { api } from "../api";

// A touch over the server's 30-min run cap (RUN_TIMEOUT_SECONDS): past this a
// "started" run is interrupted, not live — so we stop polling it. Matches the
// boundary ScheduledTasksPanel uses to decide "Running…" vs "Run now".
const RUN_TIMEOUT_MS = 31 * 60 * 1000;

/**
 * Scheduled tasks for the active environment (per-user: the API only returns
 * the caller's own tasks). Mirrors useMcpServers: list + mutations, refetched
 * on environment change, plus a poll while any run is in flight so a finished
 * run flips the row back from "Running…" to "Run now".
 */
export function useScheduledTasks(envId) {
  const [tasks, setTasks] = useState([]);
  const [category, setCategory] = useState("personal"); // "personal" | "lt"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!envId) { setTasks([]); return; }
    if (!silent) setLoading(true);
    try {
      setTasks(await api.listScheduledTasks(envId, category));
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [envId, category]);

  useEffect(() => { refresh(); }, [refresh]);

  // While a task is mid-run, its row shows a disabled "Running…" button. The
  // run finishes server-side with no client signal, so poll until the status
  // settles (then the effect tears the interval down). Silent refresh avoids a
  // "Loading…" flicker on every tick.
  const anyRunning = tasks.some(
    (t) => t.last_run_status === "started" && t.last_run_at &&
      Date.now() - new Date(t.last_run_at).getTime() < RUN_TIMEOUT_MS
  );
  useEffect(() => {
    if (!anyRunning) return undefined;
    const id = setInterval(() => { refresh({ silent: true }); }, 5000);
    return () => clearInterval(id);
  }, [anyRunning, refresh]);

  const createTask = useCallback(async (body) => {
    await api.createScheduledTask(envId, body);
    await refresh();
  }, [envId, refresh]);

  const updateTask = useCallback(async (taskId, body) => {
    await api.updateScheduledTask(envId, taskId, body);
    await refresh();
  }, [envId, refresh]);

  const deleteTask = useCallback(async (taskId) => {
    await api.deleteScheduledTask(envId, taskId);
    await refresh();
  }, [envId, refresh]);

  const runTaskNow = useCallback(async (taskId) => {
    await api.runScheduledTaskNow(envId, taskId);
    await refresh();
  }, [envId, refresh]);

  return { tasks, category, setCategory, loading, error, refresh,
           createTask, updateTask, deleteTask, runTaskNow };
}
